import { useEffect, useMemo } from 'react'
import { type Content, EditorContent, useEditor } from '@tiptap/react'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Document } from '@tiptap/extension-document'
import { Text } from '@tiptap/extension-text'
import { History } from '@tiptap/extension-history'
import { Link } from '@/components/notes/TipTapNotes/plugins/Link'

export type ProjectNotesEditorProps = {
  value?: string
  onChange: (value: string) => void
  readOnly?: boolean
}

const editorClassName = 'min-h-12 max-h-48 overflow-auto focus:outline-none pb-2 [&>p]:!m-0'

export const ProjectNotesEditor = ({ value = '', onChange, readOnly = false }: ProjectNotesEditorProps) => {
  const content = useMemo(() => textToJson(value), [value])
  const editor = useEditor({
    extensions: [Document, Paragraph, Text, History, Link],
    content, // This is a default value, but the EditorContent component is not controlled
    onUpdate: ({ editor, transaction }) => {
      if (transaction.docChanged) {
        const text = editor.getText()
        onChange(text)
      }
    },
    onFocus: ({ editor }) => {
      editor.setOptions({
        editorProps: {
          attributes: {
            class: editorClassName,
            spellcheck: 'true',
          },
        },
      })
    },
    onBlur: ({ editor }) => {
      editor.setOptions({
        editorProps: {
          attributes: {
            class: editorClassName,
            spellcheck: 'false',
          },
        },
      })
    },
    editorProps: {
      attributes: {
        class: editorClassName,
        spellcheck: 'false',
      },
    },
    editable: !readOnly,
  })

  useEffect(() => {
    if (editor) {
      const currentValue = editor.getText()
      if (currentValue === value) {
        return
      }
      const selection = editor.state.selection
      // Set the content of the editor to the new value while preserving the selection
      editor.chain().setContent(textToJson(value)).setTextSelection(selection).run()
    }
  }, [editor, value])

  return <EditorContent editor={editor} />
}

function textToJson(text: string): Content {
  const paragraphs = text.split('\n').map(paragraphText => ({
    type: 'paragraph',
    content: paragraphText ? [{ type: 'text', text: paragraphText }] : [],
  }))
  return { type: 'doc', content: paragraphs }
}
